import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listPacks, createPack, updatePack, deletePack, clearToken, getToken, adminMe, apiErr } from "@/lib/adminApi";
import { imgUrl, fmtPrice, BADGE } from "@/lib/site";
import { PackForm } from "@/components/admin/PackForm";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Loader2, ExternalLink } from "lucide-react";

export const AdminDashboard = () => {
  const nav = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => listPacks().then((r) => setPacks(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    adminMe().then(load).catch(() => { clearToken(); nav("/admin/login"); });
  }, [nav]);

  const onSave = async (payload, editId) => {
    if (editId) {
      await updatePack(editId, payload);
      toast.success("Pack aktualisiert");
    } else {
      await createPack(payload);
      toast.success("Pack erstellt");
    }
    await load();
  };

  const confirmDelete = async () => {
    try {
      await deletePack(toDelete.id);
      toast.success("Pack gelöscht");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const logout = () => { clearToken(); nav("/admin/login"); };

  useEffect(() => {
    if (!formOpen && !toDelete) {
      const t = setTimeout(() => { document.body.style.pointerEvents = ""; }, 120);
      return () => clearTimeout(t);
    }
  }, [formOpen, toDelete]);

  return (
    <div data-testid="admin-dashboard" className="relative z-[2] min-h-screen">
      <header className="sticky top-0 z-40 bg-[#0f0f11]/80 backdrop-blur-xl border-b border-white/10">
        <div className="mx-auto max-w-[1100px] px-6 h-16 flex items-center justify-between">
          <span className="font-display font-extrabold tracking-tight text-lg">OG VAULT · Admin</span>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#8b8b93] hover:text-white text-sm font-mono"><ExternalLink size={14} /> Shop</a>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-1.5 text-[#8b8b93] hover:text-white text-sm font-mono"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-extrabold text-3xl">Packs verwalten</h1>
            <p className="text-[#8b8b93] mt-1 text-sm">{packs.length} Packs im Shop</p>
          </div>
          <button
            data-testid="admin-add-pack"
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="flex items-center gap-2 bg-[#e2ff00] text-black font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full hover:bg-white transition-colors"
          >
            <Plus size={16} /> Neues Pack
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-[#e2ff00]" /></div>
        ) : (
          <div className="space-y-3">
            {packs.map((p) => {
              const b = BADGE[p.badge] || BADGE.premium;
              return (
                <div key={p.id} data-testid={`admin-row-${p.slug}`} className="flex items-center gap-4 border border-white/10 bg-[#0f0f11] rounded-xl p-4">
                  {p.image ? (
                    <img src={imgUrl(p.image)} alt={p.name} className="w-14 h-16 object-cover object-top rounded border border-white/10" />
                  ) : (
                    <div className="w-14 h-16 rounded border border-dashed border-white/15 grid place-items-center text-[9px] text-[#54545c]">kein Bild</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border" style={{ color: b.color, borderColor: b.color }}>{b.label}</span>
                      <h3 className="font-display font-bold truncate">{p.name}</h3>
                    </div>
                    <p className="text-sm text-[#8b8b93] truncate">{p.subtitle}</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="font-display font-bold">{fmtPrice(p.price)}</div>
                    {p.original_price && <div className="text-xs text-[#8b8b93] line-through">{fmtPrice(p.original_price)}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`admin-edit-${p.slug}`} onClick={() => { setEditing(p); setFormOpen(true); }} className="p-2.5 rounded-lg border border-white/10 hover:border-white/40 transition-colors"><Pencil size={15} /></button>
                    <button data-testid={`admin-delete-${p.slug}`} onClick={() => setToDelete(p)} className="p-2.5 rounded-lg border border-white/10 hover:border-[#ff0055] hover:text-[#ff0055] transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PackForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSave={onSave} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0f0f11] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Pack löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8b8b93]">
              „{toDelete?.name}" wird dauerhaft aus dem Shop entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/10">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={confirmDelete} className="bg-[#ff0055] text-white hover:bg-[#ff0055]/80">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
